/** Groups dependent, already-performed commands into one undo entry. */

import type { UndoCommand, UndoOptions, SerializedCommand } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import type { TraceScene } from '../../core/TraceScene';
import { deserializeCommand, registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'Composite';

interface CompositePayload {
    label: string;
    commands: SerializedCommand[];
    settle?: string[];
}

export interface CompositeOptions {
    /** Objects left unfinished between children; layout waits until all children finish. */
    settle?: Array<{ object: SceneObject; path: string }>;
}

interface CompositeReplay {
    scene: TraceScene;
    pending: SerializedCommand[];
}

/** Hold objects in their load window so layout only sees the completed operation. */
export async function whileSettling<T>(objects: SceneObject[], run: () => Promise<T>): Promise<T> {
    const held = objects.filter(object => !object.transform.isDisposed());
    for (const object of held) object._beginBehaviorLoad();
    try {
        return await run();
    } finally {
        for (const object of held) object._endBehaviorLoad();
    }
}

export class CompositeCommand implements UndoCommand {
    readonly label: string;
    private built: UndoCommand[];
    private applied: number;
    private pending: SerializedCommand[];
    private scene: TraceScene | null;
    private settling: Array<{ ref: SceneObjectRef; path: string }>;
    readonly serialize?: () => SerializedCommand;

    constructor(label: string, commands: UndoCommand[], options?: CompositeOptions);
    constructor(label: string, commands: UndoCommand[], options: CompositeOptions & { replay: CompositeReplay });
    constructor(label: string, commands: UndoCommand[], options: CompositeOptions & { replay?: CompositeReplay } = {}) {
        const replay = options.replay;
        if (commands.length === 0 && !replay?.pending.length) {
            throw new Error('[CompositeCommand] requires at least one child command');
        }
        this.label = label;
        this.built = commands;
        this.applied = replay ? 0 : commands.length;
        this.pending = replay?.pending ?? [];
        this.scene = replay?.scene ?? null;
        this.settling = (options.settle ?? []).map(({ object, path }) => ({
            ref: object.traceScene.objectIdentityRegistry.refFor(object),
            path,
        }));
        if (commands.every(command => typeof command.serialize === 'function')) {
            this.serialize = (): SerializedCommand => ({
                type: CODEC_TYPE,
                data: {
                    label: this.label,
                    commands: [...this.built.map(command => command.serialize!()), ...this.pending],
                    ...(this.settling.length > 0 ? { settle: this.settling.map(held => held.path) } : {}),
                } satisfies CompositePayload,
            });
        }
    }

    async undo(options?: UndoOptions): Promise<void> {
        await this.settled(async () => {
            const half = this.built[this.applied];
            if (half?.isPartlyApplied?.()) await half.undo(options);
            while (this.applied > 0) {
                await this.built[this.applied - 1].undo(options);
                this.applied -= 1;
            }
        });
    }

    async redo(): Promise<void> {
        await this.settled(async () => {
            const half = this.built[this.applied - 1];
            if (half?.isPartlyApplied?.()) await half.redo();
            for (;;) {
                if (this.applied === this.built.length) {
                    if (this.pending.length === 0) return;
                    this.built.push(deserializeCommand(this.pending[0], this.scene!));
                    this.pending.shift();
                }
                await this.built[this.applied].redo();
                this.applied += 1;
            }
        });
    }

    isPartlyApplied(): boolean {
        const total = this.built.length + this.pending.length;
        return (this.applied > 0 && this.applied < total) || (this.built[this.applied]?.isPartlyApplied?.() ?? false);
    }

    coalesce(other: UndoCommand): boolean {
        if (this.applied !== this.built.length || this.pending.length > 0) return false;
        return this.built[this.built.length - 1].coalesce?.(other) ?? false;
    }

    private settled(run: () => Promise<void>): Promise<void> {
        const objects = this.settling.flatMap(({ ref }) => {
            const object = ref.get();
            return object ? [object] : [];
        });
        return whileSettling(objects, run);
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): CompositeCommand => {
    const payload = data as CompositePayload;
    const settle = (payload.settle ?? []).flatMap(path => {
        const object = scene.resolveSceneObject(path);
        return object ? [{ object, path }] : [];
    });
    return new CompositeCommand(payload.label, [], {
        ...(settle.length > 0 ? { settle } : {}),
        replay: { scene, pending: [...payload.commands] },
    });
});
